import { useTranslation } from 'react-i18next'
import { useStore } from '../store'
import { useLanguage } from '../hooks/useLanguage'
import type { ViewName } from '../store'
import type { User } from '../types'
import { APP_VERSION } from '../version'
import {
  type Section, VIEW_SECTION, SECTION_DEFAULT, type SubNavItem, SubNavBtn,
} from './AppNavigation'
import { ScenarioDropdownBtn } from './ScenarioDropdownBtn'
import { GlobalSearch } from './GlobalSearch'
import { PresenceAvatars } from './PresenceAvatars'
import type { ReconciliationSession } from '../types'

type Props = {
  theme: 'light' | 'dark'
  toggleTheme: () => void
  onLogout: () => void
}

function buildSubNav(
  activeSection: Section | null,
  currentSession: ReconciliationSession | null,
  currentUser: User | null,
  t: (key: string) => string,
): SubNavItem[] {
  const sales005SubNav: SubNavItem[] = [
    { view: 'upload',           label: t('app_header.subnav_compare') },
    { view: 'result',           label: t('app_header.subnav_result'),        condition: !!currentSession },
    { view: 'history',          label: t('app_header.subnav_history') },
    { view: 'scenarios',        label: t('app_header.subnav_scenarios') },
    { view: 'replace-profiles', label: t('app_header.subnav_test_replacements') },
    { view: 'pricelists',       label: t('app_header.subnav_pricelists'),     adminOnly: true },
  ]

  const insightsSubNav: SubNavItem[] = [
    { view: 'validator',  label: t('app_header.subnav_validate') },
    { view: 'bulk-rename', label: t('app_header.subnav_bulk_rename') },
  ]

  const testdataSubNav: SubNavItem[] = [
    { view: 'testdata-generator', label: t('app_header.subnav_generator') },
    { view: 'party-presets',      label: t('app_header.subnav_examples'), adminOnly: true },
  ]

  const beheerSubNav: SubNavItem[] = [
    { view: 'users',          label: t('app_header.subnav_users'),    adminOnly: true },
    { view: 'logs',           label: t('app_header.subnav_logs'),     adminOnly: true },
    { view: 'admin-settings', label: t('app_header.subnav_settings'), adminOnly: true },
    { view: 'audit-log',      label: t('app_header.subnav_auditlog'), adminOnly: true },
  ]

  const rawSubNav =
    activeSection === 'sales005' ? sales005SubNav :
    activeSection === 'insights' ? insightsSubNav :
    activeSection === 'testdata' ? testdataSubNav :
    activeSection === 'beheer'   ? beheerSubNav   : []

  return rawSubNav.filter(item =>
    (item.condition === undefined || item.condition) &&
    (!item.adminOnly || currentUser?.is_admin)
  )
}

export function AppHeader({ theme, toggleTheme, onLogout }: Props) {
  const { t } = useTranslation()
  const { activeView, setActiveView, currentSession, currentUser } = useStore()
  const { language, changeLanguage } = useLanguage()

  const effectiveView: ViewName = (activeView === 'result' && !currentSession) ? 'upload' : activeView
  const activeSection: Section | null = VIEW_SECTION[effectiveView] ?? null
  const subNav = buildSubNav(activeSection, currentSession, currentUser, t)

  return (
    <header
      className="bg-white dark:bg-gray-900 border-b border-blue-100 dark:border-gray-800 transition-colors duration-200"
      style={{ boxShadow: '0 1px 3px 0 rgba(64,128,192,0.08)' }}
    >
      {/* Rij 1: brand + hoofd-tabs + rechter controls */}
      <div className="px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <img src="/logo.png" alt={t('app_header.logo_alt')} className="h-8 w-auto" />
          <span className="text-xs text-gray-400/70 dark:text-gray-600/70 font-mono select-none">v{APP_VERSION}</span>
          <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 shrink-0" />

          <nav className="flex items-center gap-0.5">
            <button
              onClick={() => setActiveView(SECTION_DEFAULT['sales005'])}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeSection === 'sales005'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {t('app_header.nav_sales005')}
            </button>

            <button
              onClick={() => setActiveView(SECTION_DEFAULT['insights'])}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeSection === 'insights'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {t('app_header.nav_insights')}
            </button>

            <button
              onClick={() => setActiveView(SECTION_DEFAULT['testdata'])}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeSection === 'testdata'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {t('app_header.nav_testdata')}
            </button>

            {currentUser?.is_admin && (
              <>
                <span className="mx-1.5 text-gray-200 dark:text-gray-700 select-none text-xs">│</span>
                <button
                  onClick={() => setActiveView(SECTION_DEFAULT['beheer'])}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    activeSection === 'beheer'
                      ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {t('app_header.nav_admin')}
                </button>
              </>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <GlobalSearch />
          <PresenceAvatars />

          <div className="flex items-center gap-0.5">
            {(['nl', 'en'] as const).map(lang => (
              <button
                key={lang}
                onClick={() => changeLanguage(lang)}
                title={lang === 'nl' ? 'Nederlands' : 'English'}
                className={`text-base leading-none rounded px-1 py-0.5 transition-all ${
                  language === lang
                    ? 'opacity-100 ring-1 ring-blue-300 dark:ring-blue-600 bg-blue-50 dark:bg-blue-900/30'
                    : 'opacity-40 hover:opacity-80'
                }`}
              >
                {lang === 'nl' ? '🇳🇱' : '🇬🇧'}
              </button>
            ))}
          </div>

          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? t('app_header.theme_switch_to_light') : t('app_header.theme_switch_to_dark')}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={t('app_header.theme_toggle_aria')}
          >
            {theme === 'dark' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>

          {currentUser && (
            <button
              onClick={() => setActiveView('account')}
              className={`text-xs transition-colors ${
                activeView === 'account'
                  ? 'text-blue-700 dark:text-blue-300 font-semibold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {currentUser.initials
                ? <span className="font-mono font-semibold">{currentUser.initials}</span>
                : currentUser.username}
              {currentUser.is_admin && (
                <span className="ml-1 text-violet-400 dark:text-violet-500">&middot; {t('app_header.admin_badge')}</span>
              )}
            </button>
          )}
          <button
            onClick={onLogout}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            {t('app_header.logout_button')}
          </button>
        </div>
      </div>

      {/* Rij 2: sub-nav */}
      {subNav.length > 0 && (
        <div className="px-6 py-1.5 bg-blue-50/40 dark:bg-gray-800/60 border-t border-blue-100/60 dark:border-gray-700/60 flex items-center gap-0.5">
          {subNav.map(item => (
            item.view === 'scenarios' ? (
              <ScenarioDropdownBtn
                key="scenarios"
                active={effectiveView === 'scenarios'}
                onNavigate={() => setActiveView('scenarios')}
              />
            ) : (
              <SubNavBtn
                key={item.view}
                label={item.label}
                active={effectiveView === item.view}
                onClick={() => setActiveView(item.view)}
              />
            )
          ))}
        </div>
      )}
    </header>
  )
}
